import React, { useEffect, useRef, useState } from "react";
import { Bot, Send, Loader2, Wallet, Sparkles, MessageCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";

const STARTERS = [
  "How do I improve my homepage SEO?",
  "Write 5 meta-description options for an AI website-builder pricing page.",
  "What is the right keyword strategy for a new SaaS launch?",
  "Critique this title: 'Best Cheap Web Hosting India 2026'",
];

function newSessionId() {
  return `seo-asst-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export default function SEOAssistant() {
  const [sessionId, setSessionId] = useState(() =>
    localStorage.getItem("optivize_seo_session") || newSessionId(),
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [wallet, setWallet] = useState(null);
  const scrollerRef = useRef(null);

  const COST = 1;

  useEffect(() => {
    localStorage.setItem("optivize_seo_session", sessionId);
    (async () => {
      try {
        const [w, h] = await Promise.all([
          api.get("/credits/me").then(r => r.data),
          api.get(`/seo/assistant/history?session_id=${encodeURIComponent(sessionId)}`).then(r => r.data),
        ]);
        setWallet(w);
        // Each row is { message, reply } — flatten into a chat list
        const list = [];
        h.forEach(row => {
          list.push({ role: "user", text: row.message });
          list.push({ role: "ai", text: row.reply });
        });
        setMessages(list);
      } catch { /* silent */ }
    })();
  }, [sessionId]);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg) return;
    if (wallet && wallet.balance < COST) { toast.error("Not enough credits"); return; }
    setInput("");
    setMessages(m => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/seo/assistant", { session_id: sessionId, message: msg });
      setMessages(m => [...m, { role: "ai", text: data.reply }]);
      // refresh wallet
      api.get("/credits/me").then(r => setWallet(r.data)).catch(() => {});
    } catch (e) {
      const err = e?.response?.data?.detail || "Assistant unavailable";
      toast.error(err);
      setMessages(m => [...m, { role: "ai", text: `_${err}_`, error: true }]);
    } finally { setBusy(false); }
  };

  const reset = () => {
    setSessionId(newSessionId());
    setMessages([]);
  };

  return (
    <div className="space-y-5" data-testid="seo-assistant-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-black text-white flex items-center gap-3" style={{fontFamily:'Outfit'}}>
            <Bot className="text-blue-400" /> AI SEO Assistant
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Senior SEO consultant in your pocket · 1 credit per message.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reset} className="card px-3 py-2 flex items-center gap-1.5 text-xs text-slate-300 hover:text-white" data-testid="seo-asst-reset-btn">
            <RotateCcw size={13}/> New chat
          </button>
          <Link to="/dashboard/credits" className="card px-4 py-2.5 flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="seo-credits-balance">
            <Wallet size={15} className="text-blue-400" />
            <span className="font-semibold text-white">{wallet?.balance ?? "—"}</span> credits
          </Link>
        </div>
      </header>

      <div className="card flex flex-col" style={{ minHeight: "60vh" }}>
        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-5 space-y-4" style={{ maxHeight: "65vh" }}>
          {messages.length === 0 && !busy && (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4">
                <Sparkles className="text-blue-400" size={28}/>
              </div>
              <h3 className="text-white text-lg font-bold">How can I help with your SEO?</h3>
              <p className="text-slate-400 text-sm mt-1">Try one of these:</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-2 max-w-2xl mx-auto">
                {STARTERS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    className="text-left px-4 py-3 rounded-xl bg-slate-900/60 border border-slate-700 hover:border-blue-500 text-slate-200 text-sm transition-colors"
                    data-testid={`seo-asst-starter-${i}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-blue-600 text-white rounded-br-sm"
                  : m.error
                    ? "bg-rose-500/10 border border-rose-500/30 text-rose-200 rounded-bl-sm"
                    : "bg-slate-800 text-slate-100 rounded-bl-sm"
              }`} data-testid={`seo-asst-msg-${m.role}`}>
                {m.text}
              </div>
            </div>
          ))}

          {busy && (
            <div className="flex justify-start">
              <div className="bg-slate-800 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-2 text-slate-300 text-sm">
                <Loader2 className="animate-spin" size={14}/> thinking…
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-slate-800 p-3 flex gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !busy && send()}
            placeholder="Ask about a URL, keyword, meta tag, content gap…"
            className="flex-1 bg-slate-950/80 border border-slate-700 focus:border-blue-500 outline-none rounded-lg px-4 py-2.5 text-white placeholder:text-slate-500 text-sm"
            data-testid="seo-asst-input"
          />
          <button onClick={() => send()} disabled={busy || !input.trim()} className="btn-primary !px-4" data-testid="seo-asst-send-btn">
            {busy ? <Loader2 className="animate-spin" size={16}/> : <><Send size={15}/> <span className="hidden sm:inline">Send</span></>}
          </button>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex items-center gap-2">
        <MessageCircle size={12}/> Session: <code className="text-slate-400">{sessionId.slice(-8)}</code>
      </div>
    </div>
  );
}
